import { Box, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";
import { Spinner } from "../../Common/Spinner";

import { AlertBar } from "../../Common/AlertBar";
import { SnackContext } from "../../SnackContext";

import { useTranslation } from "react-i18next";
import { read, utils } from "xlsx";
import ExampleFile from "../../../Assets/Other/esimerkki.csv";
import {
	isValidEmail,
	isValidLicensePlate
} from "../../../Helpers/Validation";

const allowedExtensions = ["csv"];

export const KeyManagementMassAddition = ({ data, handleRefresh }) => {
	const { ApiCall } = useContext(ApiContext);
	const { t } = useTranslation();
	const { showSnackMessage } = useContext(SnackContext);

	const [rows, setRows] = useState([]);
	const [invalidRows, setInvalidRows] = useState([]);
	const [file, setFile] = useState("");
	const [parsing, setParsing] = useState(false);
	const [loading, setLoading] = useState(false);
	const [invalidFileType, setInvalidFileType] = useState(false);
	const [isDragActive, setIsDragActive] = useState(false);
	const [isDragRejected, setIsDragRejected] = useState(false);
	const dragCounter = useRef(0);

	const isValidCsvPhone = (phone) => /^\+?[0-9]{7,15}$/.test(String(phone).trim());

	const parseDate = (date) => {
		const formats = [
			/^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
			/^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
			/^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
			/^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
			/^\d{2}\.\d{2}\.\d{4}$/ // DD.MM.YYYY
		];
	
		for (let i = 0; i < formats.length; i++) {
			if (formats[i].test(date)) {
				const parts = date.split(/[-\/.]/);
				let year, month, day;
	
				if (i === 0 || i === 2) { // YYYY-MM-DD or YYYY/MM/DD
					year = parts[0];
					month = parts[1] - 1;
					day = parts[2];
				} else { // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
					year = parts[2];
					month = parts[1] - 1;
					day = parts[0];
				}
	
				const parsedDate = new Date(year, month, day);
				if (!isNaN(parsedDate)) {
					return parsedDate.toISOString();
				}
			}
		}
	
		return null;
	};

	const isAllowedFile = (inputFile) => {
		if (!inputFile?.name) {
			return false;
		}

		const splittedFile = inputFile.name.split(".");
		const fileExtension =
			splittedFile[splittedFile.length - 1]?.toLowerCase();

		return allowedExtensions.includes(fileExtension);
	};

	const getDraggedFileStatus = (e) => {
		const dragItems = Array.from(e.dataTransfer?.items || []).filter(
			(item) => item.kind === "file"
		);
		if (dragItems.length === 0) {
			return e.dataTransfer?.types?.includes("Files")
				? "unknown"
				: "invalid";
		}

		const draggedFile =
			dragItems[0].getAsFile?.() || e.dataTransfer?.files?.[0];
		if (draggedFile?.name) {
			return isAllowedFile(draggedFile) ? "valid" : "invalid";
		}

		const mimeType = dragItems[0].type?.toLowerCase();
		if (mimeType === "text/csv" || mimeType === "application/csv") {
			return "valid";
		}
		if (
			mimeType === "" ||
			mimeType === "text/plain" ||
			mimeType === "application/vnd.ms-excel"
		) {
			return "unknown";
		}

		return "invalid";
	};

	function validateRow(row) {
		const hasEmail = row.email != null && String(row.email).trim() !== "";
		const hasPhone = row.phone != null && String(row.phone).trim() !== "";
		const hasLicensePlate = row.license_plate != null && String(row.license_plate).trim() !== "";

		// Some identifier must be provided
		if (!hasEmail && !hasPhone && !hasLicensePlate) {
			return false;
		}
		// Email is present but not valid
		if (hasEmail && !isValidEmail(row.email)) {
			return false;
		}
		if (hasPhone && !isValidCsvPhone(row.phone)) {
			return false;
		}
		// License plate is present but not valid
		if (hasLicensePlate && !isValidLicensePlate(String(row.license_plate).toUpperCase())) {
			return false;
		}
		// From date is present but not valid
		if (row.from !== undefined && parseDate(row.from) == null) {
			return false;
		}
		// To date is present but not valid
		if (row.to !== undefined && parseDate(row.to) == null) {
			return false;
		}

		return true;
	}

	const handleImport = () => {
		let reader = new FileReader();
		reader.onload = function () {
			let text = reader.result;
			let workbook = read(text, { type: "string", raw: true });

			let sheet_name_list = workbook.SheetNames;
			let xlData = utils.sheet_to_json(
				workbook.Sheets[sheet_name_list[0]]
			);

			let temp = [];
			let tempInvalid = [];
			for (let i = 0; i < xlData.length; i++) {
				console.log(xlData[i]);
				let row = xlData[i];
				if (validateRow(row)) {
					row.email = row.email != null ? "" + row.email : "";
					row.phone = row.phone != null ? "" + row.phone : "";
					row.license_plate = row.license_plate != null ? "" + row.license_plate : "";
					row.from = row.from === "" ? null : parseDate(row.from);
					row.to = row.to === "" ? null : parseDate(row.to);

					// in case of numeric description, convert to string
					row.description = row.description != null ? "" + row.description : "";

					temp.push(row);
				} else {
					tempInvalid.push(row);
				}
			}
			setRows(temp);
			setInvalidRows(tempInvalid);
			setParsing(false);
		};

		reader.readAsText(file, "UTF-8");
	};
	
	useEffect(() => {
		if (file !== "") {
			handleImport();
		}
	}, [file]);

	const processSelectedFile = (inputFile) => {
		setRows([]);
		setInvalidRows([]);
		setParsing(false);

		if (!inputFile) {
			return;
		}

		if (!isAllowedFile(inputFile)) {
			setInvalidFileType(true);
			setFile("");
			return;
		}

		setInvalidFileType(false);
		setParsing(true);
		setFile(inputFile);
	};

	const handleFileChange = (e) => {
		processSelectedFile(e.target.files?.[0]);
		e.target.value = "";
	};

	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current += 1;
		const dragStatus = getDraggedFileStatus(e);
		setIsDragRejected(dragStatus === "invalid");
		setIsDragActive(dragStatus !== "invalid");
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const dragStatus = getDraggedFileStatus(e);
		setIsDragRejected(dragStatus === "invalid");
		setIsDragActive(dragStatus !== "invalid");
		if (e.dataTransfer) {
			e.dataTransfer.dropEffect =
				dragStatus === "invalid" ? "none" : "copy";
		}
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current -= 1;
		if (dragCounter.current <= 0) {
			dragCounter.current = 0;
			setIsDragActive(false);
			setIsDragRejected(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current = 0;
		setIsDragActive(false);
		setIsDragRejected(false);
		processSelectedFile(e.dataTransfer.files?.[0]);
	};

	function getInvalidRowsText(rows) {
		let text = t("key.invalid_rows") + ": " + rows.length + "\n";

		for (let i = 0; i < rows.length; i++) {
			text = text + "\n" + rows[i].name + ". ";
		}
		return text;
	}

	const handleSave = () => {
		setLoading(true);
		ApiCall("POST", "client/residents/" + data.id, rows)
			.then(() => {
				handleRefresh();
				showSnackMessage("general", 200);
				setLoading(false);
				setRows([]);
				setFile("");
				setInvalidRows([]);
			})
			.catch(() => {
				// console.log(error);
				setLoading(false);
			});
	};

	useEffect(() => {
		setRows([]);
		setInvalidRows([]);
		setFile("");
		setParsing(false);
		setInvalidFileType(false);
		setIsDragActive(false);
		setIsDragRejected(false);
		dragCounter.current = 0;
	}, [data]);

	return (
		<>
			<div
				style={{
					width: "100%",
					backgroundColor: ColorScheme.secondary,
					padding: "20px",
					boxSizing: "border-box"
				}}
			>
				<Typography sx={{ color: ColorScheme.black, fontWeight: "700" }}>
					{t("key.add_from_csv")}
				</Typography>
				<Box sx={{ flexDirection: "row", display: "flex" }}>
					<Typography
						sx={{
							color: ColorScheme.textSecondary,
							fontSize: "14px",
							fontWeight: "400"
						}}
					>
						{t("key.download_example_file")}
					</Typography>
					<div style={{ width: "5px" }}></div>
					<a href={ExampleFile} download target="_blank" rel="noreferrer">
						<Typography sx={{ fontSize: "14px" }}>
							{"csv"}
						</Typography>
					</a>
				</Box>
				<div style={{ height: "10px" }}></div>
				{invalidFileType && (
					<AlertBar
						textContent={t("key.selected_file_not_csv")}
						type="error"
						showAlert={true}
						standardVariant={"standard"}
					></AlertBar>
				)}

				{file === "" && (
					<>
						<Box
							component="label"
							htmlFor="csvInput"
							onDragEnter={handleDragEnter}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							sx={{
								display: "block",
								width: "100%"
							}}
						>
							<Box
								sx={{
									width: "100%",
									height: "50px",
									boxSizing: "border-box",
									backgroundColor: isDragRejected
										? ColorScheme.dangerSecondary
										: isDragActive
										? ColorScheme.secondary
										: ColorScheme.primary,
									border:
										isDragActive || isDragRejected
											? "2px dashed"
											: "none",
									borderColor: isDragRejected
										? ColorScheme.dangerPrimary
										: isDragActive
										? ColorScheme.black
										: ColorScheme.primary,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: "4px",
									marginTop: "20px",
									cursor: "pointer",
									transition:
										"background-color 0.2s ease, border-color 0.2s ease"
								}}
							>
								<Typography
									sx={{
										fontSize: "15px",
										color: isDragRejected
											? ColorScheme.dangerPrimary
											: isDragActive
											? ColorScheme.textSecondary
											: ColorScheme.buttonPrimaryText
									}}
								>
									{isDragRejected
										? t("key.invalid_file_type")
										: isDragActive
										? t("key.drop_csv")
										: t("key.upload_csv")}
								</Typography>
							</Box>
						</Box>
						<input
							onChange={handleFileChange}
							id="csvInput"
							name="file"
							type="file"
							accept=".csv"
							style={{
								display: "none"
							}}
						/>
					</>
				)}
				{file !== "" && (
					<>
						<Typography
							sx={{
								color: ColorScheme.black,
								fontWeight: "700",
								marginTop: "10px",
								fontSize: "14px"
							}}
						>
							{file.name}
						</Typography>
						<div style={{ height: "10px" }}></div>
						{parsing && (
							<Spinner
								small
								color={ColorScheme.primary}
							></Spinner>
						)}
						{rows.length > 0 && (
							<AlertBar
								textContent={
									t("key.valid_rows") + ": " + rows.length
								}
								type="info"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						{invalidRows.length > 0 && rows.length > 0 && (
							<AlertBar
								whiteSpacePreLine={true}
								textContent={getInvalidRowsText(invalidRows)}
								type="warning"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						{rows.length === 0 && (
							<AlertBar
								textContent={t("key.no_valid_rows_in_file")}
								type="error"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						{rows.length > 50 && (
							<AlertBar
								textContent={t("key.too_many_rows_in_file")}
								type="error"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						{rows.length <= 50 && rows.length > 10 && (
							<AlertBar
								textContent={t("key.large_user_add_guide")}
								type="info"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						{rows.length + data.user_count > data.user_limit && (
							<AlertBar
								textContent={t("key.no_space_for_all_users")}
								type="error"
								showAlert={true}
								standardVariant={"standard"}
							></AlertBar>
						)}
						<div
							style={{
								flexDirection: "row",
								display: "flex",
								justifyContent: "space-between"
							}}
						>
							<Box
								onClick={() => {
									setFile("");
									setRows([]);
									setInvalidRows([]);
								}}
								sx={{
									width: "100%",
									height: "50px",
									border:
										"1px solid " + ColorScheme.buttonPrimary,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: "4px",
									marginTop: "20px",
									cursor: "pointer"
								}}
							>
								<Typography
									sx={{ fontSize: "15px", color: ColorScheme.black }}
								>
									{t("buttons.cancel")}
								</Typography>
							</Box>
							<div style={{ width: "20px" }}></div>
							<Box
								onClick={() => {
									if (
										rows.length > 0 &&
										rows.length + data.user_count <=
											data.user_limit && rows.length <= 50
											&& !loading
									) {
										handleSave();
									}
								}}
								sx={{
									width: "100%",
									height: "50px",
									backgroundColor: ColorScheme.primary,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: "4px",
									marginTop: "20px",
									cursor:
										rows.length > 0 &&
										rows.length + data.user_count <=
											data.user_limit && rows.length <= 50 && !loading && 
										"pointer",
									opacity:
										rows.length > 0 &&
										rows.length + data.user_count <=
											data.user_limit && rows.length <= 50 && !loading 
											? 1
											: 0.5
								}}
							>
								{loading ? (
									<Spinner small color={ColorScheme.secondary}></Spinner>
								) : (
									<Typography sx={{ fontSize: "15px" }}>
										{t("key.add_users")}
									</Typography>
								)}
							</Box>
						</div>
					</>
				)}
			</div>
		</>
	);
};
